import React, { useState } from 'react';
import xCloseIcon from '../../assets/icons/x-close.svg';
import arrowUpRightIcon from '../../assets/icons/arrow-up-right.svg';
import chevronRightIcon from '../../assets/icons/chevron-right.svg';
import pageAboutIcon from '../../assets/icons/page-about.svg';
import pageTeamIcon from '../../assets/icons/page-team.svg';
import pageWhatweDoIcon from '../../assets/icons/page-whatwedo.svg';
import pageDocsIcon from '../../assets/icons/page-documents.svg';
import pageHiringIcon from '../../assets/icons/page-hiring.svg';
import pageNoticesIcon from '../../assets/icons/page-notices.svg';
import pageHelpIcon from '../../assets/icons/page-help.svg';

/* ── Page type → nav config ──────────────────────────────── */
const PAGE_CONFIG = {
    about_us:    { label: 'About Us',    icon: pageAboutIcon },
    team:        { label: 'Team',        icon: pageTeamIcon },
    what_we_do:  { label: 'What We Do',  icon: pageWhatweDoIcon },
    documents:   { label: 'Documents',   icon: pageDocsIcon },
    hiring:      { label: 'Hiring',      icon: pageHiringIcon },
    notices:     { label: 'Notices',     icon: pageNoticesIcon },
    help:        { label: 'Help',        icon: pageHelpIcon },
};

const AVATAR_GRADS = [
    'linear-gradient(135deg,#667eea,#764ba2)',
    'linear-gradient(135deg,#f093fb,#f5576c)',
    'linear-gradient(135deg,#4facfe,#00f2fe)',
    'linear-gradient(135deg,#43e97b,#38f9d7)',
    'linear-gradient(135deg,#fa709a,#fee140)',
];

/* ── Page renderers ──────────────────────────────────────── */
const AboutUsPage = ({ minisite }) => (
    <div>
        <div className="ms-preview-section">
            <div className="ms-preview-section-header">
                <div className="ms-preview-section-icon">
                    <img src={pageAboutIcon} alt="" />
                </div>
                <div className="ms-preview-section-title">About Us</div>
            </div>
            <div className="ms-preview-section-body">
                <p>
                    Welcome to <strong>{minisite.title}</strong>. We are dedicated to delivering
                    exceptional value and innovation to our clients and partners.
                </p>
                <p>
                    Our team is passionate about what we do and committed to excellence in
                    every project we undertake. We believe in transparency, collaboration,
                    and continuous improvement.
                </p>
                <p>
                    Founded with a mission to make a difference, we have grown into a
                    trusted partner for businesses of all sizes around the world.
                </p>
            </div>
        </div>
        <div className="ms-preview-section">
            <div className="ms-preview-section-title ms-preview-values-heading">Our Values</div>
            <div className="ms-preview-values-grid">
                {['Innovation', 'Integrity', 'Collaboration', 'Excellence'].map((v, i) => (
                    <div
                        key={v}
                        className="ms-preview-value-card"
                        style={{ borderTop: `3px solid ${['#667eea','#f5576c','#43e97b','#4facfe'][i]}` }}
                    >
                        <div className="ms-preview-value-name">{v}</div>
                        <div className="ms-preview-value-desc">
                            A core principle that guides everything we do.
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const TeamPage = ({ minisite }) => {
    const demoMembers = [
        { name: 'Alex Johnson', role: 'CEO & Founder', init: 'AJ' },
        { name: 'Sarah Chen',   role: 'Head of Product', init: 'SC' },
        { name: 'Marcus Lee',   role: 'Lead Engineer', init: 'ML' },
        { name: 'Priya Patel',  role: 'Design Lead', init: 'PP' },
        { name: 'James Walker', role: 'Sales Director', init: 'JW' },
        { name: 'Emily Carter', role: 'Marketing Manager', init: 'EC' },
    ];

    return (
        <div className="ms-preview-section">
            <div className="ms-preview-section-header">
                <div className="ms-preview-section-icon">
                    <img src={pageTeamIcon} alt="" />
                </div>
                <div className="ms-preview-section-title">Meet the Team</div>
            </div>
            <div className="ms-preview-team-grid">
                {demoMembers.map((m, i) => (
                    <div key={m.name} className="ms-preview-team-card">
                        <div
                            className="ms-preview-team-avatar"
                            style={{ background: AVATAR_GRADS[i % AVATAR_GRADS.length] }}
                        >
                            {m.init}
                        </div>
                        <div className="ms-preview-team-name">{m.name}</div>
                        <div className="ms-preview-team-role">{m.role}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const WhatWeDoPage = () => {
    const services = [
        { title: 'Product Development', desc: 'End-to-end product strategy and execution from idea to launch.' },
        { title: 'Consulting Services', desc: 'Expert guidance to help your business grow and scale efficiently.' },
        { title: 'Digital Transformation', desc: 'Modernising your processes with cutting-edge technology.' },
        { title: 'Training & Support', desc: 'Upskilling your team and providing ongoing dedicated support.' },
    ];

    return (
        <div className="ms-preview-section">
            <div className="ms-preview-section-header">
                <div className="ms-preview-section-icon">
                    <img src={pageWhatweDoIcon} alt="" />
                </div>
                <div className="ms-preview-section-title">What We Do</div>
            </div>
            <div className="ms-preview-wwd-list">
                {services.map((s, i) => (
                    <div key={s.title} className="ms-preview-wwd-item">
                        <div
                            className="ms-preview-wwd-icon"
                            style={{ background: AVATAR_GRADS[i % AVATAR_GRADS.length] }}
                        >
                            {i + 1}
                        </div>
                        <div>
                            <div className="ms-preview-wwd-title">{s.title}</div>
                            <div className="ms-preview-wwd-desc">{s.desc}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const DocumentsPage = () => {
    const docs = [
        { name: 'Company Handbook 2024', size: '2.4 MB', type: 'PDF' },
        { name: 'Brand Guidelines', size: '8.1 MB', type: 'PDF' },
        { name: 'Privacy Policy', size: '142 KB', type: 'PDF' },
        { name: 'Terms of Service', size: '98 KB', type: 'PDF' },
        { name: 'Product Roadmap Q1 2025', size: '1.2 MB', type: 'PPTX' },
    ];

    return (
        <div className="ms-preview-section">
            <div className="ms-preview-section-header">
                <div className="ms-preview-section-icon">
                    <img src={pageDocsIcon} alt="" />
                </div>
                <div className="ms-preview-section-title">Documents</div>
            </div>
            <div className="ms-preview-docs-list">
                {docs.map((d) => (
                    <div key={d.name} className="ms-preview-doc-row">
                        <div className="ms-preview-doc-left">
                            <div className="ms-preview-doc-type">{d.type}</div>
                            <div>
                                <div className="ms-preview-doc-name">{d.name}</div>
                                <div className="ms-preview-doc-size">{d.size}</div>
                            </div>
                        </div>
                        <img src={arrowUpRightIcon} alt="Download" className="ms-preview-doc-download" />
                    </div>
                ))}
            </div>
        </div>
    );
};

const HiringPage = () => {
    const jobs = [
        { title: 'Senior Frontend Engineer', dept: 'Engineering', type: 'Full-time', location: 'Remote' },
        { title: 'Product Manager', dept: 'Product', type: 'Full-time', location: 'Hybrid' },
        { title: 'UX Designer', dept: 'Design', type: 'Full-time', location: 'On-site' },
        { title: 'Marketing Specialist', dept: 'Marketing', type: 'Contract', location: 'Remote' },
    ];

    return (
        <div className="ms-preview-section">
            <div className="ms-preview-section-header">
                <div className="ms-preview-section-icon">
                    <img src={pageHiringIcon} alt="" />
                </div>
                <div className="ms-preview-section-title">Open Positions</div>
            </div>
            <div className="ms-preview-job-list">
                {jobs.map((j) => (
                    <div key={j.title} className="ms-preview-job-card">
                        <div className="ms-preview-job-info">
                            <div className="ms-preview-job-title">{j.title}</div>
                            <div className="ms-preview-job-meta">{j.dept} · {j.location}</div>
                        </div>
                        <div className="ms-preview-job-tags">
                            <span className="ms-preview-tag">{j.type}</span>
                            <span className="ms-preview-tag">{j.location}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const NoticesPage = () => {
    const notices = [
        {
            title: 'Office Closure — Public Holiday',
            body: 'Our offices will be closed on 25th December. Emergency support remains available.',
            date: 'Dec 20, 2024',
        },
        {
            title: 'System Maintenance Scheduled',
            body: 'Planned maintenance on Sunday 22nd December from 02:00 – 06:00 UTC. Services may be intermittent.',
            date: 'Dec 18, 2024',
        },
        {
            title: 'New Product Update Released',
            body: 'Version 3.2.0 is now live. See the release notes for a full list of improvements and bug fixes.',
            date: 'Dec 15, 2024',
        },
    ];

    return (
        <div className="ms-preview-section">
            <div className="ms-preview-section-header">
                <div className="ms-preview-section-icon">
                    <img src={pageNoticesIcon} alt="" />
                </div>
                <div className="ms-preview-section-title">Notices</div>
            </div>
            <div className="ms-preview-notice-list">
                {notices.map((n) => (
                    <div key={n.title} className="ms-preview-notice-card">
                        <div className="ms-preview-notice-title">{n.title}</div>
                        <div className="ms-preview-notice-body">{n.body}</div>
                        <div className="ms-preview-notice-date">{n.date}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const HelpPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const faqs = [
        {
            q: 'How do I get started?',
            a: 'Getting started is easy. Sign up for an account, complete your profile, and follow the onboarding guide to set up your workspace.',
        },
        {
            q: 'What are the system requirements?',
            a: 'Our platform runs in any modern web browser (Chrome, Firefox, Safari, Edge). No additional software installation required.',
        },
        {
            q: 'How do I reset my password?',
            a: 'Click "Forgot password" on the login page and enter your email address. You will receive a reset link within a few minutes.',
        },
        {
            q: 'Can I invite team members?',
            a: 'Yes! Navigate to Settings > Team and click "Invite Members". You can invite unlimited members on paid plans.',
        },
        {
            q: 'Where can I find billing information?',
            a: 'Billing details are available under Settings > Billing. You can view invoices, update payment methods and manage your subscription.',
        },
    ];

    return (
        <div className="ms-preview-section">
            <div className="ms-preview-section-header">
                <div className="ms-preview-section-icon">
                    <img src={pageHelpIcon} alt="" />
                </div>
                <div className="ms-preview-section-title">Help & FAQ</div>
            </div>
            <div className="ms-preview-faq-list">
                {faqs.map((f, i) => (
                    <div key={i} className="ms-preview-faq-item">
                        <button
                            className="ms-preview-faq-question"
                            onClick={() => setOpenFaq(openFaq === i ? null : i)}
                        >
                            {f.q}
                            <img
                                src={chevronRightIcon}
                                alt=""
                                style={{ transform: openFaq === i ? 'rotate(90deg)' : 'none' }}
                            />
                        </button>
                        {openFaq === i && (
                            <div className="ms-preview-faq-answer">{f.a}</div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

/* ── Page type → component map ──────────────────────────── */
const PAGE_RENDERERS = {
    about_us:   AboutUsPage,
    team:       TeamPage,
    what_we_do: WhatWeDoPage,
    documents:  DocumentsPage,
    hiring:     HiringPage,
    notices:    NoticesPage,
    help:       HelpPage,
};

/* ── Main Preview Component ─────────────────────────────── */
const MinisitePreview = ({ minisite, onClose, onPublish, isOwner }) => {
    const pages = minisite.pages || [];

    /* Build nav items — from real pages or fallback demo set */
    const navItems = pages.length > 0
        ? pages.map((p) => {
            const type = p.type || p.page_type || 'about_us';
            const cfg = PAGE_CONFIG[type] || { label: p.title || type, icon: pageAboutIcon };
            return { type, label: cfg.label, icon: cfg.icon };
        })
        : Object.entries(PAGE_CONFIG).map(([type, cfg]) => ({ type, ...cfg }));

    const [activeTab, setActiveTab] = useState(navItems[0]?.type || 'about_us');

    const ActivePage = PAGE_RENDERERS[activeTab] || AboutUsPage;

    /* Cover gradient index */
    const gradIdx = (minisite.id || 0) % 6;
    const gradMap = [
        'linear-gradient(135deg,#667eea,#764ba2)',
        'linear-gradient(135deg,#f093fb,#f5576c)',
        'linear-gradient(135deg,#4facfe,#00f2fe)',
        'linear-gradient(135deg,#43e97b,#38f9d7)',
        'linear-gradient(135deg,#fa709a,#fee140)',
        'linear-gradient(135deg,#a18cd1,#fbc2eb)',
    ];

    return (
        <div className="ms-preview-overlay" onClick={onClose}>
            <div className="ms-preview-container" onClick={(e) => e.stopPropagation()}>
                {/* Top bar */}
                <div className="ms-preview-topbar">
                    <button className="ms-icon-btn" onClick={onClose}>
                        <img src={xCloseIcon} alt="Close" />
                    </button>
                    <div className="ms-preview-topbar-title">{minisite.title}</div>
                    <span className="ms-preview-topbar-slug">/{minisite.slug}</span>

                    <div className="ms-preview-topbar-actions">
                        {isOwner && minisite.status === 'draft' && (
                            <button
                                className="ms-btn-primary"
                                onClick={() => onPublish && onPublish(minisite)}
                            >
                                <img src={arrowUpRightIcon} alt="" />
                                Publish
                            </button>
                        )}
                        {minisite.status === 'published' && (
                            <span className="ms-card-status ms-status-published">
                                <span className="ms-card-status-dot" />
                                Published
                            </span>
                        )}
                    </div>
                </div>

                {/* Layout */}
                <div className="ms-preview-layout">
                    {/* Sidebar nav */}
                    <nav className="ms-preview-nav">
                        <div className="ms-preview-nav-label">Navigation</div>
                        {navItems.map((item) => (
                            <button
                                key={item.type}
                                className={`ms-preview-nav-item ${activeTab === item.type ? 'active' : ''}`}
                                onClick={() => setActiveTab(item.type)}
                            >
                                <img src={item.icon} alt={item.label} />
                                {item.label}
                            </button>
                        ))}
                    </nav>

                    {/* Content */}
                    <div className="ms-preview-content">
                        {/* Hero — background is runtime-computed per minisite, stays inline */}
                        <div className="ms-preview-hero" style={{ background: gradMap[gradIdx] }}>
                            <div className="ms-preview-hero-content">
                                <div className="ms-preview-hero-title">{minisite.title}</div>
                                {minisite.team && (
                                    <div className="ms-preview-hero-subtitle">
                                        {minisite.team.name}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Active page */}
                        <ActivePage minisite={minisite} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MinisitePreview;
